import { Platform, ScrollView, View } from "@-/com.npmjs.react-native";
import { ScopedVariables } from "@-/com.npmjs.uniwind";
import {
  getQuizManifestEntryBySlug,
  isQuizAnswerCorrect,
  type QuizManifestEntry,
} from "@-/dev.improve.haniverse.assets.slides";
import { getQuizComponents } from "@-/dev.improve.haniverse.assets.slides/components";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import { useCardScrollOptIn } from "@-/dev.improve.haniverse.lib.cards.review-swipe";
import { useDeviceSetting } from "@-/dev.improve.haniverse.lib.device-settings";
import { learnCardPresentationKey } from "@-/dev.improve.haniverse.lib.learn.card-action";
import { getSlideTextSizeCSSVariables } from "@-/dev.improve.haniverse.lib.slide.text-size";
import {
  LearnFlashcardStage,
  type LearnFlashcardStageProps,
} from "@-/dev.improve.haniverse.routes.nav.app.learn.deck._components.card";
import {
  type LearnCardControls,
  LearnFlashcardSurfaceReadyMarker,
  useRegisterLearnCardControls,
} from "@-/dev.improve.haniverse.routes.nav.app.learn.deck.context";
import { slideMdxComponents } from "@-/dev.improve.haniverse.routes.nav.app.learn.lesson.slide.index";
import { useCallback, useMemo, useState } from "react";

import { QuizCardContext, type QuizCardContextValue } from "#quiz-card-context";
import { QuizOptionDeclaration, QuizOptions, QuizStatusLine } from "#quiz-options";
import { shuffleQuizOptions } from "#shuffle-quiz-options";

/** A quiz body is a slide's prose plus its `<Quiz>`; `<QuizOption>` only declares options. */
const quizMdxComponents = {
  ...slideMdxComponents,
  Quiz: QuizOptions,
  QuizOption: QuizOptionDeclaration,
};

function QuizFlashcardControls({ controls }: { controls: LearnCardControls }) {
  useRegisterLearnCardControls(controls);
  return null;
}

/** The quiz's one face: its prompt and options, then the result once checked. */
function QuizCardFront({ quiz }: { quiz: QuizManifestEntry }) {
  const QuizContent = getQuizComponents()[quiz.key];
  const { isScrollEnabled } = useCardScrollOptIn();
  const [slideTextSize] = useDeviceSetting("slideTextSize");
  const textSizeVariables = useMemo(
    () => getSlideTextSizeCSSVariables(slideTextSize, Platform.OS === "web" ? "web" : "native"),
    [slideTextSize],
  );
  if (QuizContent === undefined) {
    throw new Error(`No quiz registered for key "${quiz.key}".`);
  }
  return (
    // Maestro tells quizzes apart by this key and answers from the committed answer key.
    <View className="relative min-h-0 flex-1 bg-surface" testID={`learn-quiz-${quiz.key}`}>
      <ScrollView
        className="flex-1"
        contentContainerClassName="grow justify-center gap-6 px-4 py-12"
        scrollEnabled={isScrollEnabled}
        showsVerticalScrollIndicator={isScrollEnabled}
      >
        {/* The prompt follows the learner's slide text size, like the slides it comes after. */}
        <ScopedVariables variables={textSizeVariables}>
          <QuizContent components={quizMdxComponents} />
        </ScopedVariables>
        <QuizStatusLine />
      </ScrollView>
      <LearnFlashcardSurfaceReadyMarker />
    </View>
  );
}

/** A slug the bundle no longer has (an old history entry): say so rather than crash the deck. */
function MissingQuizFront() {
  return (
    <View className="flex-1 items-center justify-center bg-surface px-6" testID="learn-quiz-missing">
      <Text className="text-center" colorVariant="muted">
        This quiz is no longer available.
      </Text>
      <LearnFlashcardSurfaceReadyMarker />
    </View>
  );
}

/**
 * An authored quiz (`:::quiz`, see `assets/slides`) on the deck: one face, no Info view. The learner
 * selects options, Check grades them against the quiz's answer key, and Continue or a swipe either
 * way commits the graded answer (`answer-quiz`): correct reviews the quiz Easy, wrong Again. The
 * swipe stays off until the answer is checked. The options are shuffled per showing.
 */
export function LearnQuizFlashcard(
  props: Omit<LearnFlashcardStageProps, "back" | "front" | "info"> & {
    slot: LearnFlashcardStageProps["slot"] & { item: { kind: "quiz" } };
  },
) {
  const { quizSlug, source } = props.slot.item;
  const quiz = getQuizManifestEntryBySlug(quizSlug);
  const presentationKey = learnCardPresentationKey(props.slot.presentation);
  const isCurrent = props.role === "current";
  const [selected, setSelected] = useState<ReadonlySet<string>>(() => new Set());
  const [answer, setAnswer] = useState<{ correct: boolean } | null>(null);

  const toggle = useCallback(
    (glyph: string) => {
      if (quiz === undefined || answer !== null) {
        return;
      }
      setSelected((previous) => {
        if (quiz.select === "one") {
          return previous.has(glyph) ? new Set() : new Set([glyph]);
        }
        const next = new Set(previous);
        if (next.has(glyph)) {
          next.delete(glyph);
        } else {
          next.add(glyph);
        }
        return next;
      });
    },
    [answer, quiz],
  );
  const check = useCallback(() => {
    if (quiz === undefined || answer !== null || selected.size === 0) {
      return;
    }
    setAnswer({ correct: isQuizAnswerCorrect(quiz, selected) });
  }, [answer, quiz, selected]);

  const controls = useMemo<LearnCardControls>(
    () => ({
      answer,
      check,
      getActionSnapshot: async () => ({ kind: "quiz" as const, quizSlug, source }),
      hasSelection: selected.size > 0,
      kind: "quiz",
    }),
    [answer, check, quizSlug, selected.size, source],
  );
  const options = useMemo(
    () => (quiz === undefined ? [] : shuffleQuizOptions(quiz.options, presentationKey)),
    [presentationKey, quiz],
  );
  const contextValue = useMemo<QuizCardContextValue | null>(
    () =>
      quiz === undefined
        ? null
        : {
            answer,
            isInteractive: isCurrent && !props.isInteractionPending,
            options,
            quiz,
            selected,
            toggle,
          },
    [answer, isCurrent, options, props.isInteractionPending, quiz, selected, toggle],
  );

  return (
    <QuizCardContext value={contextValue}>
      {isCurrent ? <QuizFlashcardControls controls={controls} /> : null}
      <LearnFlashcardStage
        {...props}
        back={null}
        front={quiz === undefined ? <MissingQuizFront /> : <QuizCardFront quiz={quiz} />}
        info={null}
      />
    </QuizCardContext>
  );
}
